// src/components/ContactSafetyModal.js
import React, { useState, useEffect } from 'react';
import './ContactSafetyModal.css';

/**
 * مودال "قبل التواصل مع صاحب الإعلان" - بيظهر لما المستخدم يدوس على زر
 * الاتصال أو الواتساب، وبيفضل زر إتمام التواصل Disabled لحد ما يوافق
 * على الإرشادات عن طريق الـ checkbox.
 *
 * props:
 * - isOpen: bool
 * - actionType: 'call' | 'whatsapp' -> بيتحكم في نص وأيقونة زر التأكيد
 * - onClose: function -> تقفيل المودال من غير تواصل
 * - onConfirm: function -> تنفيذ عملية الاتصال/الواتساب الفعلية بعد الموافقة
 * - onReport: function -> زر الإبلاغ (مش بيعمل حاجة حاليًا)
 * - currentLanguage: 'ar' | 'en'
 * - getLocalizedText: function(ar, en)
 */
function ContactSafetyModal({
  isOpen,
  actionType,
  onClose,
  onConfirm,
  onReport,
  currentLanguage,
  getLocalizedText,
  adLink
}) {
  const [understood, setUnderstood] = useState(false);
  const t = (ar, en) => getLocalizedText(ar, en);

  // Reset الموافقة كل ما المودال يتفتح من جديد
  useEffect(() => {
    if (isOpen) setUnderstood(false);
  }, [isOpen]);

  if (!isOpen) return null;

  const points = [
    t(
      'لا تدفع أي مبلغ مقدمًا. لا تدفع عربونًا أو رسوم حجز أو أي تحويل مالي قبل معاينة المكان في الواقع.',
      "Don't pay anything in advance. Don't pay a deposit, booking fee, or  asked to do any money transfer before viewing the place in reality."
    ),
    t(
      'إذا طلب منك أي مبلغ بحجة الحجز قبل المعاينة في الواقع، فهناك احتمال كبير أن تكون محاولة احتيال. في هذه الحالة، لا تُكمل التعامل وقم بالإبلاغ عن الإعلان.',
      "If you're asked for any fees before viewing the place in reality, it's very likely a scam attempt. In that case, stop the interaction and report the Ad."
    ),
    t(
      'إذا طلب منك ناشر الإعلان أي عمولة أو رسوم سمسرة، قم بالإبلاغ عن الإعلان، لأن السمسرة والعمولات مخالفة لسياسة المنصة.',
      'If the Ad publisher asks you to pay any commission or brokerage fee,  Please report the Ad, as brokerage activities and commissions are against our policies.'
    ),
    t(
      'إذا طلب ناشر الإعلان أي رسوم إضافية غير مذكورة في السعر الأساسي للإعلان، فيُرجى الإبلاغ عن الإعلان.',
      'If the Ad publisher asks for any extra fees not mentioned in the main price of the Ad, please report the Ad.'
    ),
    t(
      'إذا كانت الصور لا تعكس الواقع أو كانت مضللة بشكل واضح، أخبرنا بذلك.',
      "If the photos don't reflect reality or are clearly misleading, let us know."
    ),
    t(
      'إذا تعرضت لأي إساءة أو معاملة غير لائقة أثناء التواصل، يُرجى إرسال بلاغ حتى نتمكن من اتخاذ الإجراء المناسب.',
      'If you experience any abuse or inappropriate behavior during communication, please send a report so we can take the right action.'
    )
  ];

  const REPORT_FORM_BASE_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSchiMdQiaVqRVlKG5uPVH_-9565dgbqHMHOPhseiIvr--ypGQ/viewform';
  const REPORT_ENTRY_ID = 'entry.723940996'; // خانة "The Link for the Ad you want to report"

  const handleReportClick = () => {
    const prefillUrl = `${REPORT_FORM_BASE_URL}?usp=pp_url&${REPORT_ENTRY_ID}=${encodeURIComponent(adLink || '')}`;
    window.open(prefillUrl, '_blank', 'noopener,noreferrer');
    if (typeof onReport === 'function') {
      onReport();
    }
  };

  const isWhatsapp = actionType === 'whatsapp';
  const confirmLabel = isWhatsapp
    ? t('واتساب', 'WhatsApp')
    : t('اتصل', 'Call');
  const confirmIconClass = isWhatsapp ? 'fab fa-whatsapp' : 'fas fa-phone';

  return (
    <div className="contact-safety-modal-overlay">
      <div
        className={`contact-safety-modal ${currentLanguage === 'ar' ? 'ar' : 'en'}`}
        dir={currentLanguage === 'ar' ? 'rtl' : 'ltr'}
      >
        <button
          className="contact-safety-modal-close"
          onClick={onClose}
          aria-label={t('إغلاق', 'Close')}
        >
          ×
        </button>

        <div className="contact-safety-modal-icon">⚠️</div>

        <h2 className="contact-safety-modal-title">
          {t('قبل التواصل مع صاحب الإعلان', 'Before Contacting the Ad publisher')}
        </h2>

        <p className="contact-safety-modal-intro">
          {t(
            'للحفاظ على سلامتك وتجربة آمنة للجميع، يُرجى الانتباه للنقاط التالية:',
            'To keep you safe and ensure a secure experience for everyone, please pay attention to the following points:'
          )}
        </p>

        <ul className="contact-safety-modal-points">
          {points.map((point, index) => (
            <li className="contact-safety-point-item" key={index}>
              {point}
            </li>
          ))}
        </ul>

        <label className="contact-safety-modal-checkbox-row">
          <input
            type="checkbox"
            checked={understood}
            onChange={(e) => setUnderstood(e.target.checked)}
          />
          <span>
            {t(
              'أفهم هذه الإرشادات، وسأقوم بالإبلاغ عن تعدي أواجهه أثناء التواصل.',
              'I understand these guidelines, and I will report any violation I encounter during communication.'
            )}
          </span>
        </label>

        <div className="contact-safety-modal-actions">
          <button
            type="button"
            className={`contact-safety-confirm-btn ${isWhatsapp ? 'whatsapp' : 'call'}`}
            disabled={!understood}
            onClick={onConfirm}
          >
            <i className={confirmIconClass}></i>
            <span>{confirmLabel}</span>
          </button>

          <button
            type="button"
            className="contact-safety-report-btn"
            onClick={handleReportClick}
          >
            <i className="fas fa-flag"></i>
            <span>{t('إبلاغ', 'Report')}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default ContactSafetyModal;